import { InfoIcon } from "@phosphor-icons/react";
import { Box, Card, Flex, Reset, Text, Tooltip } from "@radix-ui/themes";

import { formatCurrency, formatSignedCurrency } from "../overview.format";
import type {
  AccountBalancesCardPresentationProps,
  BalanceRow,
  BalanceSection,
} from "../types";

const UNAVAILABLE = "—";

export const AccountBalancesCardPresentation = ({
  accountId,
  sections,
  isLoading,
  hasError,
}: AccountBalancesCardPresentationProps) => {
  // Left column is "how much can I spend", right is "what am I holding and
  // what does holding it cost" — so the first section stands alone.
  const [spendingPower, ...holdings] = sections;
  const columns: BalanceSection[][] = [
    spendingPower ? [spendingPower] : [],
    holdings,
  ];

  return (
    <Card size="2" className="flex-1">
      <Flex direction="column" gap="4" height="100%">
        <Flex direction="column" gap="1">
          <Text size="4" weight="bold">
            Account balances
          </Text>
          <Text size="1" color="gray" className="atd-numeric">
            {accountId}
          </Text>
        </Flex>

        {hasError ? (
          <Text size="2" color="red">
            Balances are unavailable.
          </Text>
        ) : (
          <Flex gap="6" wrap="wrap" flexGrow="1">
            {columns.map((columnSections, columnIndex) => (
              <Flex
                // biome-ignore lint/suspicious/noArrayIndexKey: fixed two-column layout, order is stable.
                key={columnIndex}
                data-testid="balances-column"
                direction="column"
                gap="4"
                justify="between"
                className="min-w-[280px] flex-[1_1_320px]"
              >
                {columnSections.map((section, index) => (
                  <Section
                    key={section.title}
                    section={section}
                    showDivider={index > 0}
                    isLoading={isLoading}
                  />
                ))}
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>
    </Card>
  );
};

const Section = ({
  section,
  showDivider,
  isLoading,
}: {
  section: BalanceSection;
  showDivider: boolean;
  isLoading: boolean;
}) => (
  <Flex direction="column" gap="3">
    {showDivider ? (
      <Box
        data-testid="balances-section-divider"
        className="h-px bg-[color:var(--gray-a5)]"
      />
    ) : null}
    <Flex direction="column" gap="2">
      <Text size="1" color="gray" className="uppercase tracking-wider">
        {section.title}
      </Text>
      <Flex direction="column" gap="2">
        {section.rows.map((row) => (
          <BalanceLine key={row.label} row={row} isLoading={isLoading} />
        ))}
      </Flex>
    </Flex>
  </Flex>
);

const BalanceLine = ({
  row,
  isLoading,
}: {
  row: BalanceRow;
  isLoading: boolean;
}) => {
  const formatted =
    row.value === undefined
      ? isLoading
        ? "…"
        : UNAVAILABLE
      : row.signed
        ? formatSignedCurrency(row.value)
        : formatCurrency(row.value);

  return (
    <Flex align="start" justify="between" gap="3">
      <Flex align="start" gap="1" minWidth="0" flexGrow="1">
        <Text size="2" weight={row.emphasize ? "medium" : "regular"}>
          {row.label}
        </Text>
        <Tooltip content={row.tooltip}>
          <Reset>
            <button
              type="button"
              aria-label={`About ${row.label}`}
              className="mt-[3px] cursor-help text-[color:var(--gray-9)] focus-visible:shadow-[inset_0_0_0_2px_var(--focus-8)]"
            >
              <InfoIcon size={13} weight="regular" />
            </button>
          </Reset>
        </Tooltip>
      </Flex>
      <Text
        size="2"
        weight={row.emphasize ? "bold" : "medium"}
        className="atd-numeric whitespace-nowrap"
      >
        {formatted}
      </Text>
    </Flex>
  );
};
